import React, { useMemo, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Container } from "@pixi/react";

import { SOUND_NAME } from "../../../../sounds";
import { PixiSettings } from "../../../../pixiSettings";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { soundManager } from "../../../../SoundManager";
import { openBonusBuy, closeMenu } from "../../../../State/actions";
import {
  selectBonusBuyActive,
  selectCriticalAnimationPlaying,
  selectHoldAndWinActive,
  selectIsSpinning,
  selectAutoPlayActive,
  selectFreeRoundsActive,
  selectUserInterfaceDisabled,
  selectFreeSpinActive,
} from "../../../../State/selectors";
import { FEATURES_CONFIG } from "../../../../featuresConfig";
import { safeInit } from "../../../../utilities";
import BuyBonusController from "./BuyBonusController";

// ============================================================================
// BUY BONUS BUTTON CONFIGURATION
// Single source of truth for scale, position per device/orientation
// ============================================================================
const BUY_BONUS_CONFIG = {
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      scale: 1.2,
      xOffset: 0.6,
      yOffset: 1.3,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      scale: 1.4,
      xOffset: 0.75,
      yOffset: 1.4,
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      scale: 1.7,
      xOffset: 1.0,
      yOffset: 1.75,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      scale: 1.7,
      xOffset: 1.0,
      yOffset: 1.5,
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: {
    scale: 1.3,
    xOffset: 0.9,
    yOffset: 1.2,
  },
};

// Base spine size before device scaling
const BASE_SIZE = 170;

const ButtonBuyBonus = ({ toolbar }) => {
  const { device, orientation, symbolScale, symbolWidth, symbolHeight } =
    useDimensions();
  const dispatch = useDispatch();
  const popupActive = useSelector(selectBonusBuyActive);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const isSpinning = useSelector(selectIsSpinning);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const freeRoundsActive = useSelector(selectFreeRoundsActive);
  const userInterfaceDisabled = useSelector(selectUserInterfaceDisabled);
  const freeSpinActive = useSelector(selectFreeSpinActive);

  const controllerRef = useRef(null);
  const containerRef = useRef(null);
  const isDisabledRef = useRef(false);
  const popupActiveRef = useRef(false);

  // Resolve device config from the single config object
  const deviceConfig = useMemo(() => {
    const cfg = BUY_BONUS_CONFIG[device];
    return cfg?.[orientation] ?? cfg ?? { scale: 1, xOffset: 0, yOffset: 0 };
  }, [device, orientation]);

  // Spine target size — BASE_SIZE scaled by symbolScale (responsive)
  const targetSize = useMemo(
    () => Math.round(BASE_SIZE * symbolScale),
    [symbolScale],
  );

  // Position — offsets are multipliers of symbolWidth/symbolHeight
  const buttonPosition = useMemo(
    () => ({
      x: (deviceConfig.xOffset || 0) * symbolWidth,
      y: (deviceConfig.yOffset || 0) * symbolHeight,
    }),
    [deviceConfig, symbolWidth, symbolHeight],
  );

  // Disabled conditions
  const isDisabled =
    userInterfaceDisabled ||
    criticalAnimationPlaying ||
    holdAndWinActive ||
    isSpinning ||
    autoPlayActive ||
    freeSpinActive ||
    (FEATURES_CONFIG.FREE_ROUNDS.ENABLED && freeRoundsActive);

  // Keep refs in sync for use in callbacks
  useEffect(() => {
    isDisabledRef.current = isDisabled;
  }, [isDisabled]);

  useEffect(() => {
    popupActiveRef.current = popupActive;
  }, [popupActive]);

  // Initialize spine controller
  useEffect(() => {
    if (containerRef.current && !controllerRef.current) {
      safeInit(() => {
        controllerRef.current = new BuyBonusController(
          containerRef.current,
          targetSize,
          targetSize,
          "BUY BONUS",
          deviceConfig,
          {
            onTap: () => {
              if (popupActiveRef.current || isDisabledRef.current) return;
              soundManager.play(SOUND_NAME.button_click);
              controllerRef.current?.playClick(() => {
                dispatch(closeMenu());
                dispatch(openBonusBuy());
              });
            },
            onHover: () => {
              if (isDisabledRef.current) return;
              controllerRef.current?.playHover();
            },
            onOut: () => {
              controllerRef.current?.returnToIdle();
            },
          },
        );
      });
    }

    return () => {
      if (controllerRef.current) {
        controllerRef.current.cleanup();
        controllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update dimensions on resize
  useEffect(() => {
    if (controllerRef.current?.isInitialized) {
      controllerRef.current.updateDimensions(
        targetSize,
        targetSize,
        deviceConfig,
      );
    }
  }, [targetSize, deviceConfig]);

  // Update disabled state
  useEffect(() => {
    if (controllerRef.current?.isInitialized) {
      controllerRef.current.setDisabled(isDisabled);
    }
  }, [isDisabled]);

  return (
    <Container
      name="primary_button_buy_bonus"
      x={buttonPosition.x}
      y={buttonPosition.y}
      zIndex={2}
    >
      <Container
        ref={containerRef}
        sortableChildren={true}
        name="buy_bonus_spine_container"
      />
    </Container>
  );
};

export default ButtonBuyBonus;
